
import React, { useState, useRef } from 'react';
import { 
  Building, 
  Users, 
  Coffee, 
  Wifi, 
  Car, 
  MapPin, 
  Star, 
  Phone,
  Mail,
  Calendar,
  Shield,
  Award,
  Clock
} from 'lucide-react';

const TrendingSearchesSection = () => {
  const scrollContainerRef = useRef(null);
  
  const features = [
    {
      id: 1,
      icon: <Building className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Premium Accommodation",
      description: "Luxury rooms with modern amenities",
      color: "from-amber-500 to-orange-500"
    },
    {
      id: 2,
      icon: <Coffee className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Multi-Cuisine Restaurant",
      description: "Authentic Indian & international cuisine",
      color: "from-orange-400 to-amber-400"
    },
    {
      id: 3,
      icon: <Wifi className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "High-speed WiFi",
      description: "Complimentary internet access",
      color: "from-amber-600 to-yellow-500"
    },
    {
      id: 4,
      icon: <Car className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Travel & Transport",
      description: "Cab services available 24/7",
      color: "from-orange-500 to-red-400"
    },
    {
      id: 5,
      icon: <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Prime Location",
      description: "Near Assi Ghat and attractions",
      color: "from-yellow-500 to-amber-500"
    },
    {
      id: 6,
      icon: <Users className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Event & Conference",
      description: "Business meetings & social events",
      color: "from-amber-400 to-orange-400"
    },
    {
      id: 7,
      icon: <Star className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Premium Service",
      description: "24/7 room service",
      color: "from-orange-600 to-amber-600"
    },
    {
      id: 8,
      icon: <Shield className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Safety & Security",
      description: "CCTV surveillance & security",
      color: "from-amber-500 to-yellow-400"
    },
    {
      id: 9,
      icon: <Award className="w-5 h-5 sm:w-6 sm:h-6" />,
      title: "Award Winning",
      description: "Excellence in hospitality",
      color: "from-orange-500 to-amber-600"
    }
  ];

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -250, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 250, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative py-8 sm:py-16 px-4 bg-gradient-to-b from-amber-50 via-orange-50 to-white overflow-hidden">
      
      {/* Background Image with Orange Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://www.hotelprakashpalace.in/img/gallery/room5.jpeg"
          alt="Hotel Prakash Palace Interior"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-amber-50/90 via-orange-50/80 to-amber-50/90"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        <div className="max-w-6xl mx-auto">
          
          {/* Header Section */}
          <div className="text-center mb-8 sm:mb-14">
            <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="w-6 sm:w-10 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"></div>
              <span className="text-amber-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">WELCOME TO</span>
              <div className="w-6 sm:w-10 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full"></div>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Hotel <span className="text-amber-700">Prakash Palace</span>
            </h1>
            
            <p className="text-gray-700 max-w-2xl mx-auto text-sm sm:text-base md:text-lg leading-relaxed">
              Experience the perfect blend of traditional Varanasi hospitality with modern luxury accommodations
            </p>
          </div>

          {/* Two Cards Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-16">
            
            {/* Left Card - Welcome */}
            <div className="group">
              <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-6 sm:p-8 border border-amber-100 hover:border-amber-300 transition-all duration-300 hover:shadow-xl sm:hover:shadow-2xl">
                <div className="flex items-center gap-4 sm:gap-5 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg">
                    <Building className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Welcome to Our Hotel</h3>
                    <div className="w-12 sm:w-16 h-1 sm:h-1.5 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"></div>
                  </div>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Hotel Prakash Palace is designed for both business as well as leisure travelers. 
                    Experience the perfect blend of traditional Indian hospitality and modern amenities in the spiritual heart of Varanasi.
                  </p>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Located just minutes away from Assi Ghat, offering easy access to all major attractions.
                  </p>
                </div>
                
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-amber-100">
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-gray-900">4.9</div>
                      <div className="text-amber-600 text-xs sm:text-sm font-medium">Rating</div>
                    </div>
                    <div className="h-6 sm:h-8 w-px bg-amber-200"></div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-gray-900">24/7</div>
                      <div className="text-amber-600 text-xs sm:text-sm font-medium">Service</div>
                    </div>
                    <div className="h-6 sm:h-8 w-px bg-amber-200"></div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-gray-900">100+</div>
                      <div className="text-amber-600 text-xs sm:text-sm font-medium">Happy Guests</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Card - Contact & Booking */}
            <div className="group">
              <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-6 sm:p-8 border border-amber-100 hover:border-amber-300 transition-all duration-300 hover:shadow-xl sm:hover:shadow-2xl">
                <div className="flex items-center gap-4 sm:gap-5 mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-amber-600 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg">
                    <Phone className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Contact & Booking</h3>
                    <div className="w-12 sm:w-16 h-1 sm:h-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"></div>
                  </div>
                </div>
                
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-gray-900 font-semibold text-sm sm:text-base">Call Us</div>
                      <div className="text-gray-700 text-xs sm:text-sm">+91 9119641118, +91 9415224017</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-gray-900 font-semibold text-sm sm:text-base">Email Us</div>
                      <div className="text-gray-700 text-xs sm:text-sm">hotelprakashpalacevns@gmail.com</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-gray-900 font-semibold text-sm sm:text-base">Location</div>
                      <div className="text-gray-700 text-xs sm:text-sm">BHU – Assi Ghat Road, Lanka, Varanasi</div>
                    </div>
                  </div>
                </div>
                
                <button className="w-full mt-6 sm:mt-8 py-2.5 sm:py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg hover:shadow-lg sm:hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                  Book Your Stay Now
                </button>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-amber-100 to-orange-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-amber-200 mb-4 sm:mb-6 shadow-sm">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <span className="text-amber-800 font-semibold text-xs sm:text-sm">Limited rooms available for next week</span>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-5">
              <button className="px-6 sm:px-8 py-2.5 sm:py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg hover:shadow-lg sm:hover:shadow-xl hover:scale-105 transition-all duration-300">
                Book Your Luxury Stay
              </button>
              <button className="px-6 sm:px-8 py-2.5 sm:py-3.5 bg-white text-gray-800 rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg border-2 border-amber-300 hover:bg-amber-50 transition-all duration-300">
                View Gallery
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Add custom CSS for scrollbar hide */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

    </div>
  );
};

export default TrendingSearchesSection;